import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useState } from "react";

import { getDeal, type Deal } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { DealRegistrationWizard } from "@/components/deals/DealRegistrationWizard";
import { InlineAlert } from "@/components/ui/inline-alert";

/**
 * CM/RA/RM on-behalf deal registration (CHP-202). Renders the same wizard as
 * the partner flow; the wizard's first step shows the "Assign this deal to:"
 * selector for managers. Managers are never BDMs, so (unlike the partner
 * route) there is no compulsory-training lockout gate here.
 */
export function ManagerDealsNewRoute() {
  const { accessToken } = useCurrentUser();
  // Resume flow: ?draftId=<uuid> to pick up an on-behalf draft.
  const search = useSearch({ strict: false }) as { draftId?: string };
  const draftId = search.draftId;

  const draft = useQuery<Deal>({
    queryKey: ["deals", "detail", draftId, accessToken],
    queryFn: () => getDeal(accessToken!, draftId!),
    enabled: !!accessToken && !!draftId,
  });
  const [draftErrorDismissed, setDraftErrorDismissed] = useState(false);

  if (draftId && draft.isLoading) {
    return (
      <p role="status" className="container py-6">
        Loading draft...
      </p>
    );
  }

  // CHP-804: a failed ?draftId fetch must signal before falling through to a
  // fresh wizard (matches the partner route).
  const showDraftError = !!draftId && draft.isError && !draftErrorDismissed;
  return (
    <>
      {showDraftError ? (
        <div className="container pt-6">
          <InlineAlert tone="warning" className="mb-4">
            <span
              className="flex items-center justify-between gap-3"
              data-testid="draft-load-error"
            >
              <span>Couldn&apos;t load that draft — starting fresh.</span>
              <button
                type="button"
                onClick={() => setDraftErrorDismissed(true)}
                aria-label="Dismiss"
                className="shrink-0 rounded p-0.5 hover:bg-black/5"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </span>
          </InlineAlert>
        </div>
      ) : null}
      <DealRegistrationWizard initialDeal={draft.data} onBehalf backTo="/dashboard/manager/deals" />
    </>
  );
}
